import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { addDays } from 'date-fns';
import { useAuthStore } from '../store/authStore';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const { setUser, setProfile, setSubscription, setPromptCount } = useAuthStore();
  const [showSupportMessage, setShowSupportMessage] = useState(false);

  const initialDays = searchParams.get('initial_days');

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (loading) {
      timeoutId = setTimeout(() => {
        setShowSupportMessage(true);
      }, 7000);
    } else {
      setShowSupportMessage(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading]);

  useEffect(() => {
    const cleanupBeforeRegistration = async () => {
      try {
        setUser(null);
        setProfile(null);
        setSubscription(null);
        setPromptCount(0);
        
        localStorage.removeItem('sb-supabase-auth-token');
        sessionStorage.removeItem('sb-supabase-auth-token');
        
        await supabase.auth.signOut();
        
        console.log('Estado de autenticação e armazenamento local limpos para novo registro');
      } catch (err) {
        console.error('Erro ao limpar estado antes do registro:', err);
      }
    };
    
    cleanupBeforeRegistration();
  }, [setUser, setProfile, setSubscription, setPromptCount]);

  const isFromValidPaymentSource = () => {
    if (typeof window === 'undefined') return false;
    
    const referrer = document.referrer.toLowerCase();
    const currentUrl = window.location.href.toLowerCase();
    
    const stripePaymentPattern = /https:\/\/buy\.stripe\.com\/[a-zA-Z0-9_]+/;
    const hasStripePaymentUrl = stripePaymentPattern.test(referrer) || stripePaymentPattern.test(currentUrl);

    const hasStripeParams = currentUrl.includes('session_id=') || 
                           currentUrl.includes('payment_intent=') ||
                           currentUrl.includes('cs_test_') ||
                           currentUrl.includes('cs_live_') ||
                           referrer.includes('cs_live_') ||
                           referrer.includes('cs_test_');

    const validDomains = [
      'stripe.com',
      'buy.stripe.com',
      'checkout.stripe.com',
      'js.stripe.com'
    ];

    return validDomains.some(domain => referrer.includes(domain)) || hasStripeParams || hasStripePaymentUrl;
  };

  const hasValidPaymentParam = () => {
    return searchParams.has('payment_complete') || 
           searchParams.has('stripe_success') || 
           searchParams.has('payment_source') ||
           searchParams.has('session_id') ||
           searchParams.has('payment_intent');
  };

  useEffect(() => {
    if (initialDays && !isFromValidPaymentSource() && !hasValidPaymentParam()) {
      setError('Link inválido. Por favor, realize o registro através da forma correta.');
      setButtonDisabled(true);
    }
  }, [initialDays]);

  const clearCacheAndRedirect = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
        
        const cachesKeys = await caches.keys();
        await Promise.all(cachesKeys.map(key => caches.delete(key)));
      }

      localStorage.clear();
      sessionStorage.clear();
      
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });

      window.location.href = `/login?fresh=${Date.now()}`;
    } catch (err) {
      console.error('Erro ao limpar cache:', err);
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    if (registrationSuccess) {
      setButtonDisabled(true);
      const timer = setTimeout(() => {
        clearCacheAndRedirect();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [registrationSuccess]);

  const calculateExpirationDate = (days: number) => {
    return addDays(new Date(), days).toISOString();
  };

  const formatDateForDb = (date: Date): string => {
    return date.toISOString().replace('T', ' ').replace('Z', '+00');
  };

  const performAutoLogin = async (email: string, password: string) => {
    try {
      await supabase.auth.signOut();
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      window.location.href = `/dashboard/targeting?fresh=${Date.now()}`;
      return true;
    } catch (err) {
      console.error('Erro ao fazer login automático:', err);
      return false;
    }
  };

  const verifyUserCredentials = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });

      if (error) {
        return { valid: false, user: null };
      }

      if (data.user) {
        await supabase.auth.signOut();
        return { valid: true, user: data.user };
      }

      return { valid: false, user: null };
    } catch (err) {
      return { valid: false, user: null };
    }
  };

  const updateSubscriptionForExistingUser = async (userId: string) => {
    if (!initialDays) return false;
    
    try {
      const days = parseInt(initialDays);
      if (![7, 15, 30, 365].includes(days)) {
        throw new Error(`Período inicial inválido: ${days}`);
      }

      const { data: subscription, error: fetchError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (fetchError) throw fetchError;

      const currentDate = new Date();
      const newExpirationDate = new Date(currentDate);
      const expirationDays = days === 15 ? 30 : days;
      newExpirationDate.setDate(currentDate.getDate() + expirationDays);

      const subscriptionData = {
        plan: days === 15 ? 'essential' : 
              days === 7 ? 'test' : 
              days === 30 ? 'pro' : 'unlimited',
        status: 'active',
        next_billing_date: formatDateForDb(newExpirationDate),
        updated_at: formatDateForDb(currentDate),
        last_renewal_date: formatDateForDb(currentDate),
        request_count: days === 15 ? 30 : (days === 7 ? 7 : (subscription?.request_count || 0))
      };

      if (!subscription) {
        const { error: insertError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            created_at: formatDateForDb(currentDate),
            ...subscriptionData
          });

        if (insertError) throw insertError;
      } else {
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update(subscriptionData)
          .eq('id', subscription.id);

        if (updateError) throw updateError;
      }

      return true;
    } catch (err) {
      setError(`Erro na atualização do plano: ${err instanceof Error ? err.message : 'Erro desconhecido'}`);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await supabase.auth.signOut();
      
      const isPaymentValid = isFromValidPaymentSource() || hasValidPaymentParam();
      const daysToAdd = initialDays ? parseInt(initialDays) : 3;

      if (initialDays && !isPaymentValid) {
        throw new Error('Link inválido. Por favor, realize o registro através da forma correta.');
      }

      const { data: existingUser, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (existingUser && !userError) {
        if (!initialDays) {
          setError('Este e-mail já está cadastrado. Por favor faça login.');
          setButtonDisabled(true);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        if (initialDays && !isPaymentValid) {
          throw new Error('Registro com dias adicionais só é permitido da forma correta');
        }

        const allowedDays = [7, 15, 30, 365];
        if (initialDays && !allowedDays.includes(daysToAdd)) {
          throw new Error('Período inicial inválido');
        }

        const { valid: isValidCredentials, user: validatedUser } = await verifyUserCredentials(email, password);
        
        if (!isValidCredentials) {
          setError('E-mail já cadastrado com senha diferente. Por favor, faça login com a senha correta ou use um e-mail diferente.');
          setButtonDisabled(true);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        const success = await updateSubscriptionForExistingUser(existingUser.id);
        if (!success) {
          throw new Error('Falha ao atualizar plano existente');
        }

        if (isPaymentValid) {
          const loginSuccess = await performAutoLogin(email, password);
          if (!loginSuccess) {
            setSuccessMessage('Plano atualizado com sucesso! Redirecionando para login...');
            setRegistrationSuccess(true);
          }
        } else {
          setSuccessMessage('Plano atualizado com sucesso! Redirecionando para login...');
          setRegistrationSuccess(true);
        }
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
          emailRedirectTo: window.location.origin + '/login?fresh=' + Date.now()
        },
      });

      if (authError) {
        if (authError.message.includes('already registered') || authError.message.includes('User already registered')) {
          setError('Este e-mail já está cadastrado. Por favor faça login.');
          setButtonDisabled(true);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }
        throw authError;
      }

      if (!authData.user) throw new Error('Falha ao criar usuário');

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authData.user.id,
          email,
          name,
          created_at: new Date().toISOString()
        });

      if (profileError) throw profileError;

      let plan = 'initial';
      let requestCount = 3;
      let finalDays = daysToAdd;

      if (initialDays) {
        switch(daysToAdd) {
          case 7:
            plan = 'test';
            finalDays = 7;
            break;
          case 15:
            plan = 'essential';
            finalDays = 30;
            requestCount = 30;
            break;
          case 30:
            plan = 'pro';
            finalDays = 30;
            break;
          case 365:
            plan = 'anual';
            finalDays = 365;
            break;
        }
      }

      const currentDate = new Date().toISOString();
      const expirationDate = calculateExpirationDate(finalDays);

      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .insert({
          user_id: authData.user.id,
          plan,
          status: 'active',
          next_billing_date: expirationDate,
          created_at: currentDate,
          updated_at: currentDate,
          last_renewal_date: currentDate,
          request_count: requestCount
        });

      if (subscriptionError) throw subscriptionError;

      if (isPaymentValid) {
        const loginSuccess = await performAutoLogin(email, password);
        if (!loginSuccess) {
          setSuccessMessage('Conta criada com sucesso! Redirecionando para login...');
          setRegistrationSuccess(true);
        }
      } else {
        setSuccessMessage('Conta criada com sucesso! Redirecionando para o login...');
        setRegistrationSuccess(true);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Ocorreu um erro durante o registro');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Criar a sua conta
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Já tem uma conta?{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              Faça login
            </Link>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/50 p-4 rounded-md">
            <p className="text-sm text-red-600 dark:text-red-200">{error}</p>
          </div>
        )}

        {registrationSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md border border-green-200 dark:border-green-800 animate-fade-in">
            <p className="text-green-600 dark:text-green-300 text-sm font-medium">
              {successMessage}
            </p>
          </div>
        )}

        {showSupportMessage && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md border border-yellow-200 dark:border-yellow-800">
            <p className="text-yellow-600 dark:text-yellow-300 text-sm font-medium">
              Caso demore mais de 01 minuto, favor entrar em contato pelo WhatsApp (34) 99668-7855 ou pelo e-mail: suporte@aiaura.com.br com os dados para a criação
            </p>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="name" className="sr-only">
                Nome completo
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Nome completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading || buttonDisabled}
              />
            </div>
            <div>
              <label htmlFor="email-address" className="sr-only">
                Endereço de e-mail
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Endereço de e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || buttonDisabled}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Senha
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || buttonDisabled}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || buttonDisabled || (initialDays && !isFromValidPaymentSource() && !hasValidPaymentParam())}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Criando conta...
                </>
              ) : (
                'Criar conta'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;